import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "path-tester" }
const _hoisted_2 = { class: "input-section" }
const _hoisted_3 = { class: "preset-paths" }
const _hoisted_4 = { class: "path-buttons" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "results" }
const _hoisted_7 = { class: "result-header" }
const _hoisted_8 = { class: "path" }
const _hoisted_9 = {
  key: 0,
  class: "error-details"
}
const _hoisted_10 = {
  key: 1,
  class: "content-preview"
}
const _hoisted_11 = { class: "upload-section" }
const _hoisted_12 = {
  key: 0,
  class: "svg-preview"
}
const _hoisted_13 = ["innerHTML"]
const _hoisted_14 = {
  key: 1,
  class: "vivus-demo"
}

import { ref, onUnmounted } from 'vue'

interface TestResult {
  path: string
  success: boolean
  error?: string
  content?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'pathTester',
  setup(__props) {

const testPath = ref('./assets/sinechart_export.svg')
const testResults = ref<TestResult[]>([])
const svgPreview = ref('')
const selectedSvg = ref('')
const vivusContainer = ref<HTMLDivElement>()

// Häufige Pfad-Varianten
const commonPaths = [
  '/assets/sinechart_export.svg',
  '/src/assets/sinechart_export.svg',
  '/public/assets/sinechart_export.svg',
  './assets/sinechart_export.svg',
  '../assets/sinechart_export.svg',
  '/sinechart_export.svg',
  '/static/sinechart_export.svg',
  '/images/sinechart_export.svg'
]

let vivusInstance: any = null

onUnmounted(() => {
  vivusInstance?.destroy()
})

const testSpecificPath = async (path: string) => {
  testPath.value = path
  await testCurrentPath()
}

const testCurrentPath = async () => {
  const path = testPath.value.trim()
  if (!path) return

  // Vorherige Ergebnisse für diesen Pfad entfernen
  testResults.value = testResults.value.filter(r => r.path !== path)

  try {
    console.log(`Teste Pfad: ${path}`)

    const response = await fetch(path)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    const content = await response.text()

    if (!content.includes('<svg')) {
      throw new Error('Datei enthält kein <svg> Element')
    }

    testResults.value.unshift({
      path,
      success: true,
      content
    })

    // Automatisch als Vorschau setzen wenn erfolgreich
    svgPreview.value = content

  } catch (error: any) {
    console.error(`Fehler bei Pfad ${path}:`, error)

    testResults.value.unshift({
      path,
      success: false,
      error: error.message
    })
  }
}

const handleFileUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  if (!file.type.includes('svg')) {
    alert('Bitte wähle eine SVG-Datei aus')
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    const content = e.target?.result as string
    svgPreview.value = content

    // Zu Testergebnissen hinzufügen
    testResults.value.unshift({
      path: `📁 ${file.name}`,
      success: true,
      content
    })
  }
  reader.readAsText(file)
}

const useThisSvg = () => {
  selectedSvg.value = svgPreview.value

  // SVG in Vivus Container einfügen
  if (vivusContainer.value) {
    vivusContainer.value.innerHTML = selectedSvg.value

    setTimeout(() => {
      initializeVivus()
    }, 100)
  }
}

const initializeVivus = async () => {
  try {
    // Dynamically import Vivus
    const { default: Vivus } = await import('vivus')

    const svgElement = vivusContainer.value?.querySelector('svg')
    if (!svgElement) {
      throw new Error('Kein SVG Element gefunden')
    }

    // SVG für Animation vorbereiten
    prepareSvgForVivus(svgElement)

    vivusInstance = new Vivus(svgElement, {
      type: 'delayed',
      duration: 200,
      start: 'manual',
      animTimingFunction: Vivus.EASE_OUT
    })

    console.log('Vivus erfolgreich initialisiert!')

  } catch (error) {
    console.error('Vivus Initialisierung fehlgeschlagen:', error)
    alert('Vivus konnte nicht initialisiert werden: ' + error.message)
  }
}

const prepareSvgForVivus = (svgElement: SVGElement) => {
  const elements = svgElement.querySelectorAll('*')

  elements.forEach((element: any) => {
    // Fills zu Strokes konvertieren
    if (element.getAttribute('fill') && element.getAttribute('fill') !== 'none') {
      element.setAttribute('stroke', element.getAttribute('fill'))
      element.setAttribute('fill', 'none')
    }

    // Default stroke für animierbare Elemente
    const animatableTags = ['path', 'line', 'polyline', 'circle', 'ellipse', 'rect', 'polygon']
    if (animatableTags.includes(element.tagName.toLowerCase())) {
      if (!element.getAttribute('stroke')) {
        element.setAttribute('stroke', '#000000')
        element.setAttribute('fill', 'none')
      }
      if (!element.getAttribute('stroke-width')) {
        element.setAttribute('stroke-width', '2')
      }
    }
  })
}

const startVivusAnimation = () => {
  vivusInstance?.reset().play()
}

const resetVivusAnimation = () => {
  vivusInstance?.reset()
}

// Beim Laden automatisch ersten Pfad testen
testSpecificPath(commonPaths[0])

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[7] || (_cache[7] = _createElementVNode("h2", null, "SVG Pfad Tester", -1)),
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("label", null, "SVG Pfad eingeben:", -1)),
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((testPath).value = $event)),
        placeholder: "z.B. /assets/sinechart_export.svg",
        class: "path-input"
      }, null, 512), [
        [_vModelText, testPath.value]
      ]),
      _createElementVNode("button", {
        onClick: testCurrentPath,
        class: "btn btn-test"
      }, "Pfad testen")
    ]),
    _createElementVNode("div", _hoisted_3, [
      _cache[2] || (_cache[2] = _createElementVNode("h3", null, "Häufige Pfade testen:", -1)),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(commonPaths, (path) => {
          return _createElementVNode("button", {
            key: path,
            onClick: ($event: any) => (testSpecificPath(path)),
            class: "btn btn-preset"
          }, _toDisplayString(path), 9, _hoisted_5)
        }), 64))
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(testResults.value, (result) => {
        return (_openBlock(), _createElementBlock("div", {
          key: result.path,
          class: "result-item"
        }, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("span", _hoisted_8, _toDisplayString(result.path), 1),
            _createElementVNode("span", {
              class: _normalizeClass(['status', result.success ? 'success' : 'error'])
            }, _toDisplayString(result.success ? '✅ Funktioniert' : '❌ Fehler'), 3)
          ]),
          (!result.success)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(result.error), 1))
            : _createCommentVNode("", true),
          (result.success && result.content)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createElementVNode("details", null, [
                  _cache[3] || (_cache[3] = _createElementVNode("summary", null, "SVG Inhalt anzeigen", -1)),
                  _createElementVNode("pre", null, _toDisplayString(result.content.substring(0, 500)) + _toDisplayString(result.content.length > 500 ? '...' : ''), 1)
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_11, [
      _cache[4] || (_cache[4] = _createElementVNode("h3", null, "Oder SVG-Datei direkt hochladen:", -1)),
      _createElementVNode("input", {
        type: "file",
        accept: ".svg",
        onChange: handleFileUpload,
        class: "file-input"
      }, null, 32)
    ]),
    (svgPreview.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          _cache[5] || (_cache[5] = _createElementVNode("h3", null, "SVG Vorschau:", -1)),
          _createElementVNode("div", {
            class: "preview-container",
            innerHTML: svgPreview.value
          }, null, 8, _hoisted_13),
          _createElementVNode("button", {
            onClick: useThisSvg,
            class: "btn btn-use"
          }, " Diese SVG für Vivus verwenden ")
        ]))
      : _createCommentVNode("", true),
    (selectedSvg.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _cache[6] || (_cache[6] = _createElementVNode("h3", null, "Vivus Animation:", -1)),
          _createElementVNode("div", {
            ref_key: "vivusContainer",
            ref: vivusContainer,
            class: "vivus-container"
          }, null, 512),
          _createElementVNode("div", { class: "vivus-controls" }, [
            _createElementVNode("button", {
              onClick: startVivusAnimation,
              class: "btn btn-animate"
            }, " Animation starten "),
            _createElementVNode("button", {
              onClick: resetVivusAnimation,
              class: "btn btn-reset"
            }, " Reset ")
          ])
        ]))
      : _createCommentVNode("", true),
    _cache[8] || (_cache[8] = _createStaticVNode("<div class=\"help-section\" data-v-6534d3df><h3 data-v-6534d3df>💡 Tipps zur Pfad-Findung:</h3><ul data-v-6534d3df><li data-v-6534d3df><strong data-v-6534d3df>Public Ordner:</strong> Dateien in <code data-v-6534d3df>public/</code> sind über <code data-v-6534d3df>/dateiname</code> erreichbar</li><li data-v-6534d3df><strong data-v-6534d3df>Unterordner:</strong> <code data-v-6534d3df>public/assets/bild.svg</code> → <code data-v-6534d3df>/assets/bild.svg</code></li><li data-v-6534d3df><strong data-v-6534d3df>Development Server:</strong> Meist läuft auf <code data-v-6534d3df>http://localhost:3000</code></li><li data-v-6534d3df><strong data-v-6534d3df>Browser-Test:</strong> Öffne <code data-v-6534d3df>http://localhost:3000/dein/pfad.svg</code> direkt</li><li data-v-6534d3df><strong data-v-6534d3df>Netzwerk-Tab:</strong> Öffne F12 → Network und schaue ob die Datei geladen wird</li></ul></div>", 1))
  ]))
}
}

})